// Relevé d'un coin d'îlot (D-303) : l'agent reste immobile au coin ; les positions reçues pendant
// 30 secondes sont moyennées pour réduire l'erreur du GPS du téléphone (3 à 10 m par mesure). Les
// mesures annoncées à plus de 50 m de précision sont écartées. Aucun suivi en dehors du relevé (D-121).
import { moyennePositions, type Coord } from '@cc/contrats';
import { positionDisponible } from './position';

export const DUREE_RELEVE_COIN_MS = 30_000;
const PRECISION_MAX_M = 50;

export interface CoinReleve {
  position: { longitude: number; latitude: number };
  echantillons: number;
  duree_s: number;
  ecart_m: number;
}

export function releverCoin(
  dureeMs = DUREE_RELEVE_COIN_MS,
  progres?: (echantillons: number, ecoule: number) => void,
): { promesse: Promise<CoinReleve>; arreter: () => void } {
  let arret = () => undefined as void;
  const promesse = new Promise<CoinReleve>((resoudre, rejeter) => {
    if (!positionDisponible()) {
      rejeter(new Error('Cet appareil n’expose pas de position.'));
      return;
    }
    const points: Coord[] = [];
    const debut = Date.now();
    let fini = false;
    const terminer = () => {
      if (fini) return;
      fini = true;
      navigator.geolocation.clearWatch(veille);
      clearTimeout(minuterie);
      if (!points.length) {
        rejeter(new Error('Aucune position reçue pendant le relevé : réessayez à découvert.'));
        return;
      }
      const m = moyennePositions(points);
      resoudre({
        position: { longitude: m.position[0], latitude: m.position[1] },
        echantillons: points.length,
        duree_s: Math.round((Date.now() - debut) / 1000),
        ecart_m: m.ecart_m,
      });
    };
    const veille = navigator.geolocation.watchPosition(
      (p) => {
        if (Number.isFinite(p.coords.accuracy) && p.coords.accuracy > PRECISION_MAX_M) return;
        points.push([p.coords.longitude, p.coords.latitude]);
        progres?.(points.length, Date.now() - debut);
      },
      (e) => {
        if (e.code === e.PERMISSION_DENIED) {
          fini = true;
          navigator.geolocation.clearWatch(veille);
          clearTimeout(minuterie);
          rejeter(new Error('La position n’est pas autorisée sur cet appareil.'));
        }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: dureeMs },
    );
    const minuterie = setTimeout(terminer, dureeMs);
    arret = terminer;
  });
  return { promesse, arreter: () => arret() };
}

/** Façade en mètres : mesure au mètre, ou nombre de pas multiplié par la longueur du pas. */
export function facadeMetres(
  methode: 'metre' | 'pas',
  valeur: number,
  longueurPas: number,
): number {
  return methode === 'metre' ? valeur : Math.round(valeur * longueurPas * 10) / 10;
}
